import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'

/**
 * Where an item's section falls in `order`: its index there, or past the end
 * for a section the order does not name and for an item without one.
 */
function sectionRank(item: MemoItem, order: string[]): number {
  const key = item.sectionTitle?.trim().toLocaleLowerCase()

  if (!key) {
    return order.length
  }

  const index = order.findIndex((section) => section.trim().toLocaleLowerCase() === key)

  return index < 0 ? order.length : index
}

/**
 * The selection with `additions` slotted in by section, one at a time: each goes
 * before the first item of its own block whose section ranks later, and at the
 * end when there is none. Equal ranks keep the order they were added in, and
 * nothing already selected moves - an order the editor arranged by hand stays.
 */
export function insertBySection(
  selected: MemoItem[],
  additions: MemoItem[],
  order: string[]
): MemoItem[] {
  if (!order.length) {
    return [...selected, ...additions]
  }

  const result = [...selected]

  for (const addition of additions) {
    const rank = sectionRank(addition, order)
    const index = result.findIndex((entry) => (
      entry.blockId === addition.blockId && sectionRank(entry, order) > rank
    ))

    if (index < 0) {
      result.push(addition)
    } else {
      result.splice(index, 0, addition)
    }
  }

  return result
}
